import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from './Modal';
import { supabase } from '../lib/supabaseClient';
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpenseModal from './ExpenseModal';
import {
  faSpinner,
  faCheck,
  faUpload,
  faEye,
  faExchangeAlt,
  faCircleCheck,
  faClock,
  faEdit,
  faTimes,
  faTrashAlt,
  faPen,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import PDFViewer from './PDFViewer';

const EXPO_PUSH_ENDPOINT = 'https://exp.host/--/api/v2/push/send';

const LoadDetailsModal = ({ isOpen, onClose, load }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [locations, setLocations] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [loadingProgress, setLoadingProgress] = useState([]);
  const [unloadingProgress, setUnloadingProgress] = useState([]);
  const [loadingCustoms, setLoadingCustoms] = useState(null);
  const [dischargeCustoms, setDischargeCustoms] = useState(null);
  const [operationOfficer, setOperationOfficer] = useState(null);
  const [loadHistory, setLoadHistory] = useState([]);
  const [historyUsers, setHistoryUsers] = useState({});

  const [isGoogleMapsLoading, setIsGoogleMapsLoading] = useState(false);

  const [selectedTaskDocument, setSelectedTaskDocument] = useState(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [allDriverLocations, setAllDriverLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [locationStartTime, setLocationStartTime] = useState('');
  const [locationEndTime, setLocationEndTime] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCompletingTask, setIsCompletingTask] = useState(false);
  const [isUndoingTask, setIsUndoingTask] = useState(false);
  const mapBounds = useRef(null);
  const [isPDFOpen, setIsPDFOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [isEditingPlates, setIsEditingPlates] = useState(false);
  const [editTowPlate, setEditTowPlate] = useState('');
  const [editTrailerPlate, setEditTrailerPlate] = useState('');
  const [isSavingPlates, setIsSavingPlates] = useState(false);


  const handleSavePlates = async () => {
    if (!load?.accepted_driver?.id) return;
    setIsSavingPlates(true);
    try {
      const updatedDriver = {
        ...load.accepted_driver,
        tow_plate: editTowPlate,
        trailer_plate: editTrailerPlate,
      };

      const { error: loadError } = await supabase
        .from('loads')
        .update({ accepted_driver: updatedDriver })
        .eq('id', load.id);

      if (loadError) throw loadError;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          tow_plate: editTowPlate,
          trailer_plate: editTrailerPlate,
        })
        .eq('id', load.accepted_driver.id);

      if (profileError) throw profileError;

      load.accepted_driver.tow_plate = editTowPlate;
      load.accepted_driver.trailer_plate = editTrailerPlate;

      setIsEditingPlates(false);
      alert('Plakalar başarıyla güncellendi.');
    } catch (error) {
      console.error('Error saving plates:', error);
      alert('Plakalar güncellenirken bir hata oluştu.');
    } finally {
      setIsSavingPlates(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const fetchLocations = async () => {
    try {
      console.log('Driver ID:', load.accepted_driver?.id);

      const last_date = load?.unloading_tasks[4].completed_at !== null ?
        new Date(load?.unloading_tasks[4].completed_at).toISOString() :
        new Date().toISOString();

      const { data: allLocations, error: allLocationsError } =
        await supabase
          .from('locations')
          .select('*')
          .eq('user_id', load.accepted_driver?.id)
          .gte("created_at", new Date(load?.loading_date).toISOString())
          .lte("created_at", last_date)
          .order('created_at', { ascending: false });

      if (allLocationsError) {
        console.error('All Locations Error:', allLocationsError);
        throw allLocationsError;
      }

      // Set all driver locations
      const locationsArray = allLocations || [];
      setAllDriverLocations(locationsArray);
      setFilteredLocations(locationsArray);

      // Set the current location (most recent one)
      if (locationsArray.length > 0) {
        const locationData = locationsArray[0];
        setLocations({
          lat: locationData.latitude || 0,
          lng: locationData.longitude || 0,
          created_at: locationData.created_at || new Date().toISOString(),
        });
      } else {
        console.log('No location data found for driver');
        setLocations({
          lat: 41.0082, // Default to Istanbul
          lng: 28.9784,
          created_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error fetching locations:', error.message);
      // Set default location on error
      setLocations({
        lat: 41.0082, // Default to Istanbul
        lng: 28.9784,
        created_at: new Date().toISOString(),
      });
    }
  };

  // Setup a real-time subscription for location updates
  useEffect(() => {
    if (load?.accepted_driver?.id) {
      setIsGoogleMapsLoading(true);
      fetchLocations();
      setTimeout(() => {
        setIsGoogleMapsLoading(false);
      }, 2000);
      

      // Set up real-time subscription for location updates
      const subscription = supabase
        .channel('locations-channel')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'locations',
            filter: `user_id=eq.${load.accepted_driver.id}`,
          },
          (payload) => {
            console.log('New location received:', payload);
            // Add the new location to the beginning of the array (most recent first)
            const newLocation = payload.new;

            setAllDriverLocations((prevLocations) => {
              const updatedLocations = [newLocation, ...prevLocations];
              return updatedLocations;
            });

            setFilteredLocations((prevLocations) => {
              // Apply any existing filters
              if (locationStartTime || locationEndTime) {
                const newDate = new Date(newLocation.created_at);
                let shouldInclude = true;

                if (locationStartTime) {
                  const startDate = new Date(locationStartTime);
                  if (newDate < startDate) shouldInclude = false;
                }

                if (locationEndTime) {
                  const endDate = new Date(locationEndTime);
                  if (newDate > endDate) shouldInclude = false;
                }

                return shouldInclude
                  ? [newLocation, ...prevLocations]
                  : prevLocations;
              }

              return [newLocation, ...prevLocations];
            });

            // Update the current location
            setLocations({
              lat: newLocation.latitude || 0,
              lng: newLocation.longitude || 0,
              created_at: newLocation.created_at || new Date().toISOString(),
            });
          }
        )
        .subscribe();

      // Clean up subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [load, locationStartTime, locationEndTime]);

  // Add new useEffect to filter locations when filter values or locations change
  useEffect(() => {
    if (allDriverLocations.length > 0) {
      let filtered = [...allDriverLocations];

      if (locationStartTime) {
        const startDate = new Date(locationStartTime);
        filtered = filtered.filter(
          (location) => new Date(location.created_at) >= startDate
        );
      }

      if (locationEndTime) {
        const endDate = new Date(locationEndTime);
        filtered = filtered.filter(
          (location) => new Date(location.created_at) <= endDate
        );
      }

      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
  }, [allDriverLocations, locationStartTime, locationEndTime]);

  useEffect(() => {
    if (load?.id) {
      const fetchProgress = async () => {
        try {
          setLoadingProgress(load.loading_tasks || []);
          setUnloadingProgress(load.unloading_tasks || []);
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      };

      fetchProgress();
    }
  }, [load?.id, load?.loading_tasks, load?.unloading_tasks]);

  useEffect(() => {
    if (load) {
      const fetchCustomsData = async () => {
        try {
          // Fetch loading customs data if loading_customs_id exists
          if (load.loading_customs_address) {
            const { data: loadingCustomsData, error: loadingError } =
              await supabase
                .from('customs')
                .select('*')
                .eq('id', load.loading_customs_address)
                .single();

            if (loadingError) throw loadingError;
            setLoadingCustoms(loadingCustomsData);
          }

          // Fetch discharge customs data if discharge_customs_id exists
          if (load.discharge_customs_address) {
            const { data: dischargeCustomsData, error: dischargeError } =
              await supabase
                .from('customs')
                .select('*')
                .eq('id', load.discharge_customs_address)
                .single();

            if (dischargeError) throw dischargeError;
            setDischargeCustoms(dischargeCustomsData);
          }
        } catch (error) {
          console.error('Error fetching customs data:', error);
        }
      };

      fetchCustomsData();
    }
  }, [load]);

  useEffect(() => {
    if (load?.operation_auth_id) {
      const fetchOperationOfficer = async () => {
        try {
          const { data, error } = await supabase
            .from('operation_auths')
            .select('*')
            .eq('id', load.operation_auth_id)
            .single();

          if (error) throw error;
          setOperationOfficer(data);
        } catch (error) {
          console.error('Error fetching operation officer:', error);
        }
      };

      fetchOperationOfficer();
    }
  }, [load?.operation_auth_id]);

  // Fetch history data
  useEffect(() => {
    const fetchHistory = async () => {
      if (!load?.id) return;

      try {
        let history = [];
        const userIds = new Set();

        // Add load creation event
        history.push({
          type: 'creation',
          timestamp: load.created_at,
          user_id: load.created_by,
          description: 'Yük oluşturuldu',
        });
        userIds.add(load.created_by);

        // Add loading tasks history
        loadingProgress.forEach((task) => {
          if (task.completed_at) {
            history.push({
              type: 'loading',
              timestamp: task.completed_at,
              user_id: task.completed_by,
              description: `Yükleme görevi tamamlandı: ${
                task.task_key === 'arrivedAtLoadingPoint'
                  ? 'Yükleme noktasına ulaşıldı'
                  : task.task_key === 'loadingStarted'
                    ? 'Yükleme başlatıldı'
                    : task.task_key === 'loadingCompleted'
                      ? 'Yükleme tamamlandı'
                      : task.task_key === 'arrivedAtExitCustoms'
                        ? 'Çıkış noktasına ulaşıldı'
                        : 'Belge yüklendi'
              }`,
            });
            userIds.add(task.completed_by);
          }
        });

        // Add unloading tasks history
        unloadingProgress.forEach((task) => {
          if (task.completed_at) {
            history.push({
              type: 'unloading',
              timestamp: task.completed_at,
              user_id: task.completed_by,
              description: `Boşaltma görevi tamamlandı: ${
                task.task_key === 'arrivedAtArrivalCustoms'
                  ? 'Varış gümrüğüne ulaşıldı'
                  : task.task_key === 'customsProceduresCompleted'
                    ? 'Gümrük işlemleri tamamlandı'
                    : task.task_key === 'arrivedAtUnloadingPoint'
                      ? 'Boşaltma noktasına ulaşıldı'
                      : task.task_key === 'deliveryDocumentsUpload'
                        ? 'Teslimat belgeleri yüklendi'
                        : 'Boşaltma tamamlandı'
              }`,
            });
            userIds.add(task.completed_by);
          }
        });

        // Sort history by timestamp (most recent first)
        history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // Fetch user information for all involved users
        if (userIds.size > 0) {
          // Fetch from users table
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, full_name')
            .in('id', Array.from(userIds));

          if (usersError) throw usersError;

          // Fetch from profiles table
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', Array.from(userIds));

          if (profilesError) throw profilesError;

          // Create a map of user information
          const userMap = {};

          // First add users with full_name
          usersData?.forEach((user) => {
            if (user.full_name) {
              userMap[user.id] = user.full_name;
            }
          });

          // Then add or override with profiles data if available
          profilesData?.forEach((profile) => {
            if (profile.first_name || profile.last_name) {
              userMap[profile.id] =
                `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            }
          });

          // For any remaining IDs without names, use a default
          Array.from(userIds).forEach((id) => {
            if (!userMap[id]) {
              userMap[id] = 'İsimsiz Kullanıcı';
            }
          });

          setHistoryUsers(userMap);
        }

        setLoadHistory(history);
      } catch (error) {
        console.error('Error fetching history:', error);
      }
    };

    fetchHistory();
  }, [load, loadingProgress, unloadingProgress]);

  // Focus map on the most recent location whenever it changes
  useEffect(() => {
    if (!locations) {
      console.log('No locations available');
      return;
    }

    // If we have a map reference and locations, focus on the most recent one
    if (mapRef.current && filteredLocations.length > 0) {
      const mostRecentLocation = filteredLocations[0];

      if (mostRecentLocation.latitude && mostRecentLocation.longitude) {
        // Center on the most recent location
        mapRef.current.setCenter({
          lat: parseFloat(mostRecentLocation.latitude),
          lng: parseFloat(mostRecentLocation.longitude),
        });

        // Set an appropriate zoom level
        mapRef.current.setZoom(14);
      }
    }
  }, [locations, filteredLocations]);

  useEffect(() => {
    // Only try to create bounds if we have locations and Google Maps is fully loaded
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.LatLngBounds &&
      allDriverLocations.length > 0
    ) {
      try {
        mapBounds.current = new window.google.maps.LatLngBounds();

        // If we have at least one location, prioritize the most recent one
        if (allDriverLocations.length > 0) {
          const mostRecentLocation = allDriverLocations[0]; // First location is the most recent

          if (
            mostRecentLocation.latitude &&
            mostRecentLocation.longitude &&
            !isNaN(mostRecentLocation.latitude) &&
            !isNaN(mostRecentLocation.longitude)
          ) {
            try {
              // Create a point for the most recent location
              const latLng = new window.google.maps.LatLng(
                parseFloat(mostRecentLocation.latitude),
                parseFloat(mostRecentLocation.longitude)
              );

              // Add the most recent location to bounds
              mapBounds.current.extend(latLng);

              // If we only have one location, create a small area around it
              if (allDriverLocations.length === 1) {
                // Create a slightly offset point to make the bounds have some area
                const offset = 0.001; // Small offset, about 100 meters
                const offsetLatLng = new window.google.maps.LatLng(
                  parseFloat(mostRecentLocation.latitude) + offset,
                  parseFloat(mostRecentLocation.longitude) + offset
                );
                mapBounds.current.extend(offsetLatLng);
              }
            } catch (err) {
              console.error(
                'Error creating LatLng for most recent location:',
                err
              );
            }
          }
        }

        // Add other locations to the bounds
        if (allDriverLocations.length > 1) {
          // Skip the first location as we've already added it
          allDriverLocations.slice(1).forEach((location) => {
            if (
              location.latitude &&
              location.longitude &&
              !isNaN(location.latitude) &&
              !isNaN(location.longitude)
            ) {
              try {
                const latLng = new window.google.maps.LatLng(
                  parseFloat(location.latitude),
                  parseFloat(location.longitude)
                );
                mapBounds.current.extend(latLng);
              } catch (err) {
                console.error('Error creating LatLng:', err);
              }
            }
          });
        }
      } catch (error) {
        console.error('Error creating LatLngBounds:', error);
        mapBounds.current = null;
      }
    }
  }, [allDriverLocations]);

  const handleTaskDocumentView = (docUrl, docType) => {
    setSelectedTaskDocument({
      url: docUrl,
      name: docType,
      category: docType,
    });
  };

  const handleSendNotification = async () => {
    if (!load?.accepted_driver?.id) {
      alert('Bu yük için sürücü atanmamış');
      return;
    }

    setIsSendingNotification(true);
    try {
      // Get driver's push token
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('push_token')
        .eq('id', load.accepted_driver.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.push_token) {
        alert('Sürücüye kayıtlı push token bulunamadı');
        return;
      }

      // Find the last incomplete task
      const allTasks = [...loadingProgress, ...unloadingProgress].sort(
        (a, b) => {
          // Sort by completed_at, null values (incomplete) come last
          if (!a.completed_at && !b.completed_at) return 0;
          if (!a.completed_at) return 1;
          if (!b.completed_at) return -1;
          return new Date(b.completed_at) - new Date(a.completed_at);
        }
      );

      const lastIncompleteTask = allTasks.find(
        (task) => task.status !== 'completed'
      );

      // Create notification message
      let notificationBody = `${load.formatted_id} referanslı yük için güncelleme`;
      if (lastIncompleteTask) {
        const taskName =
          {
            arrivedAtLoadingPoint: 'Yükleme noktasına ulaşma',
            loadingStarted: 'Yükleme başlatma',
            loadingCompleted: 'Yükleme tamamlama',
            arrivedAtExitCustoms: 'Çıkış gümrüğüne ulaşma',
            documentsUpload: 'Belge yükleme',
            arrivedAtArrivalCustoms: 'Varış gümrüğüne ulaşma',
            customsProceduresCompleted: 'Gümrük işlemlerini tamamlama',
            arrivedAtUnloadingPoint: 'Boşaltma noktasına ulaşma',
            deliveryDocumentsUpload: 'Teslimat belgelerini yükleme',
            unloadingComplete: 'Boşaltma tamamlama',
          }[lastIncompleteTask.task_key] || lastIncompleteTask.task_key;

        notificationBody = `${load.formatted_id}: Lütfen '${taskName}' görevini tamamlayınız`;
      }

      // Send notification via Expo's Push API
      try {
        await fetch(EXPO_PUSH_ENDPOINT, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            to: profileData.push_token,
            title: 'Yük Güncellemesi',
            body: notificationBody,
            sound: 'default',
            priority: 'high',
          }),
        });

        alert('Bildirim gönderildi');
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        throw new Error('Bildirim gönderilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert(error.message || 'Bildirim gönderilemedi');
    } finally {
      setIsSendingNotification(false);
    }
  };

  // Store map reference to allow focusing on new locations
  const mapRef = useRef(null);

  const handleMapLoad = useCallback(
    (map) => {
      // Store the map reference for later use
      mapRef.current = map;

      try {
        // Always prioritize focusing on the most recent location (index 0)
        if (filteredLocations.length > 0) {
          const mostRecentLocation = filteredLocations[0];

          if (mostRecentLocation.latitude && mostRecentLocation.longitude) {
            // First center on the most recent location
            map.setCenter({
              lat: parseFloat(mostRecentLocation.latitude),
              lng: parseFloat(mostRecentLocation.longitude),
            });

            // Then fit bounds if we have multiple locations
            if (mapBounds.current && filteredLocations.length > 1) {
              map.fitBounds(mapBounds.current);

              // After fitting bounds, ensure the most recent location is visible
              // by panning back to it if needed
              setTimeout(() => {
                map.panTo({
                  lat: parseFloat(mostRecentLocation.latitude),
                  lng: parseFloat(mostRecentLocation.longitude),
                });
                map.setZoom(14); // Set a reasonable zoom level
              }, 100);
            } else {
              // If we only have one location or no bounds, set an appropriate zoom level
              map.setZoom(14);
            }
          }
        }
      } catch (error) {
        console.error('Error setting map bounds:', error);
      }
    },
    [mapBounds, filteredLocations]
  );

  const renderMap = () => {

    if (isGoogleMapsLoading) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
          <div className="h-[400px] rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <p>Harita yükleniyor...</p>
          </div>
        </div>
      );
    }

    // If there are no locations, show a message
    if (!filteredLocations.length) {
      return (
        <div className="my-6">
          <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
          <div className="h-[400px] rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <p>Konum bilgisi bulunamadı</p>
          </div>
        </div>
      );
    }

    // Default center coordinates in case locations is null
    const defaultCenter = { lat: 41.0082, lng: 28.9784 }; // Default to Istanbul coordinates

    // Always prioritize the most recent location for the initial center
    let mapCenter = defaultCenter;

    if (filteredLocations.length > 0) {
      const mostRecentLocation = filteredLocations[0];
      if (mostRecentLocation.latitude && mostRecentLocation.longitude) {
        mapCenter = {
          lat: parseFloat(mostRecentLocation.latitude),
          lng: parseFloat(mostRecentLocation.longitude),
        };
      }
    } else if (locations) {
      mapCenter = { lat: locations.lat, lng: locations.lng };
    }

    const minDate = load?.loading_date
                  ? new Date(load.loading_date).toISOString().slice(0, 16)
                  : new Date().toISOString().slice(0, 16)

    const maxDate = load?.status === "assigned"
                  ? new Date().toISOString().slice(0, 16)
                  : (load?.status === "completed" 
                    ? new Date(load?.unloading_tasks[5]?.completed_at).toISOString().slice(0, 16)
                    : new Date().toISOString().slice(0, 16))

    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
        <div className="flex space-x-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm text-gray-500 mb-1">
              Başlangıç Zamanı
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border rounded-lg"
              value={locationStartTime}
              onChange={(e) => setLocationStartTime(e.target.value)}
              step="60"
              data-format="24h"
              min={minDate}
              max={maxDate}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-gray-500 mb-1">
              Bitiş Zamanı
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border rounded-lg"
              value={locationEndTime}
              onChange={(e) => setLocationEndTime(e.target.value)}
              step="60"
              data-format="24h"
              min={minDate}
              max={maxDate}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setLocationStartTime('');
                setLocationEndTime('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Filtreyi Temizle
            </button>
          </div>
        </div>
        <div className="h-[400px] rounded-lg overflow-hidden">
          <APIProvider apiKey="AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o">
            <Map
              onLoad={handleMapLoad}
              defaultCenter={mapCenter}
              defaultZoom={10}
              gestureHandling="greedy"
              options={{
                zoomControl: true,
                mapTypeControl: true,
                scaleControl: true,
                streetViewControl: true,
                rotateControl: true,
                fullscreenControl: true,
              }}
            >
              {/* Render older locations first (they'll be at the bottom) */}
              {filteredLocations.slice(1).map((location, index) => (
                <Marker
                  key={index + 1}
                  position={{ lat: location.latitude, lng: location.longitude }}
                  onClick={() => setShowInfoWindow(index + 1)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#EF4444" stroke="#DC2626" stroke-width="2"/>
                      </svg>
                    `)}`,
                    scaledSize: { width: 24, height: 24 },
                    anchor: { x: 12, y: 12 },
                  }}
                  options={{
                    zIndex: 1, // Lower z-index for older locations
                  }}
                />
              ))}

              {/* Render the most recent location last (it'll be on top) */}
              {filteredLocations.length > 0 && (
                <Marker
                  key="latest"
                  position={{
                    lat: filteredLocations[0].latitude,
                    lng: filteredLocations[0].longitude,
                  }}
                  onClick={() => setShowInfoWindow(0)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                        <circle cx="16" cy="16" r="14" fill="#3B82F6" stroke="#2563EB" stroke-width="3"/>
                        <circle cx="16" cy="16" r="6" fill="#ffffff" stroke="#2563EB" stroke-width="1"/>
                      </svg>
                    `)}`,
                    scaledSize: { width: 32, height: 32 },
                    anchor: { x: 16, y: 16 },
                  }}
                  options={{
                    zIndex: 999, // Higher z-index for the latest location
                  }}
                />
              )}
              {showInfoWindow !== false &&
                filteredLocations[showInfoWindow] && (
                  <InfoWindow
                    position={{
                      lat: filteredLocations[showInfoWindow].latitude,
                      lng: filteredLocations[showInfoWindow].longitude,
                    }}
                    onCloseClick={() => setShowInfoWindow(false)}
                    options={{
                      zIndex: 1000, // Make sure InfoWindow is always on top
                    }}
                  >
                    <div style={{ padding: '8px' }}>
                      <p style={{ margin: '0', fontWeight: 'bold' }}>
                        {showInfoWindow === 0 ? 'Son Konum' : 'Önceki Konum'}
                      </p>
                      <p style={{ margin: '4px 0 0' }}>
                        {formatDate(
                          filteredLocations[showInfoWindow].created_at
                        )}
                      </p>
                    </div>
                  </InfoWindow>
                )}
            </Map>
          </APIProvider>
        </div>
        <div className="flex items-center mt-2 text-sm">
          <div className="flex items-center mr-4">
            <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
            <span>Son Konum</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
            <span>Önceki Konumlar</span>
          </div>
          <div className="ml-auto text-sm text-gray-600">
            {filteredLocations.length} konum gösteriliyor
          </div>
        </div>
      </div>
    );
  };

  // Function to handle document upload
  const handleDocumentUpload = async (item, docType) => {
    try {
      // Create a file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,application/pdf';

      // Trigger the file selection dialog
      fileInput.click();

      // Handle file selection
      fileInput.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (limit to 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(
            "Dosya boyutu çok büyük. Lütfen 10MB'dan küçük bir dosya seçin."
          );
          return;
        }

        // Set uploading state to true
        setIsUploading(true);

        // Create a unique filename
        const fileName = `${Date.now()}_${file.name}`;

        try {
          // Upload the file to Supabase storage
          const { error } = await supabase.storage
            .from('documents')
            .upload(fileName, file);

          if (error) {
            console.error('Error uploading file:', error);
            alert('Dosya yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
            setIsUploading(false);
            return;
          }

          // Get the public URL of the uploaded file
          const {
            data: { publicUrl },
          } = supabase.storage.from('documents').getPublicUrl(fileName);

          // Update the task with the document URL
          let updatedTasks;
          if (activeTab === 'loading') {
            updatedTasks = [...loadingProgress];
            const taskIndex = updatedTasks.findIndex(
              (task) => task.task_key === item.task_key
            );

            if (taskIndex !== -1) {
              if (!updatedTasks[taskIndex].uploaded_documents) {
                updatedTasks[taskIndex].uploaded_documents = {};
              }
              updatedTasks[taskIndex].uploaded_documents[docType] = publicUrl;

              // Update the database
              const { error: updateError } = await supabase
                .from('loads')
                .update({
                  loading_tasks: updatedTasks,
                })
                .eq('id', load.id);

              if (updateError) {
                console.error(
                  'Error updating task with document URL:',
                  updateError
                );
                alert('Belge bilgisi güncellenirken bir hata oluştu.');
                setIsUploading(false);
                return;
              }

              // Update local state
              setLoadingProgress(updatedTasks);
            }
          } else if (activeTab === 'unloading') {
            updatedTasks = [...unloadingProgress];
            const taskIndex = updatedTasks.findIndex(
              (task) => task.task_key === item.task_key
            );

            if (taskIndex !== -1) {
              if (!updatedTasks[taskIndex].uploaded_documents) {
                updatedTasks[taskIndex].uploaded_documents = {};
              }
              updatedTasks[taskIndex].uploaded_documents[docType] = publicUrl;

              // Update the database
              const { error: updateError } = await supabase
                .from('loads')
                .update({
                  unloading_tasks: updatedTasks,
                })
                .eq('id', load.id);

              if (updateError) {
                console.error(
                  'Error updating task with document URL:',
                  updateError
                );
                alert('Belge bilgisi güncellenirken bir hata oluştu.');
                setIsUploading(false);
                return;
              }

              // Update local state
              setUnloadingProgress(updatedTasks);
            }
          }

          alert(`${docType} başarıyla yüklendi.`);
          setIsUploading(false);
        } catch (uploadError) {
          console.error('Error in document upload process:', uploadError);
          alert('Belge yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
          setIsUploading(false);
        }
      };
    } catch (error) {
      console.error('Error initiating document upload:', error);
      alert('Belge yükleme işlemi başlatılamadı. Lütfen tekrar deneyin.');
      setIsUploading(false);
    }
  };

  const handleUndoTask = async (item) => {
    try {
      console.log('Undoing task:', item);
      
      // Check if load is completed and ask for confirmation
      if (load.status === 'completed') {
        const confirmed = window.confirm(
          'Bu yük tamamlanmış durumda. Görevi geri aldığınızda yük "Tamamlanmamış" olarak işaretlenecek. Onaylıyor musunuz?'
        );
        
        if (!confirmed) {
          return; // User cancelled, exit the function
        }
      }
      
      // Set undoing task state to true to show loading indicator
      setIsUndoingTask(true);

      // Update the task status
      let updatedTasks;
      if (activeTab === 'loading') {
        updatedTasks = [...loadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].status = 'pending';
          updatedTasks[taskIndex].completed_at = null;
          updatedTasks[taskIndex].completed_by = null;
          if(item.requires_document) {
            if(item.task_key === 'documentsUpload') {
              updatedTasks[taskIndex].uploaded_documents = {};
            }
            else if(item.task_key === 't1DocumentUpload') {
              updatedTasks[taskIndex].uploaded_documents = { t1Document: null };
            }
          }

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              loading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating task status:', updateError);
            alert('Görev durumu güncellenirken bir hata oluştu.');
            setIsUndoingTask(false);
            return;
          }

          // Check if load status should be changed from "completed" to "assigned"
          const hasAnyPendingTask = 
            updatedTasks.some(task => task.status === 'pending') ||
            unloadingProgress.some(task => task.status === 'pending');

          if (load.status === 'completed' && hasAnyPendingTask) {
            const { error: statusUpdateError } = await supabase
              .from('loads')
              .update({ status: 'assigned' })
              .eq('id', load.id);

            if (statusUpdateError) {
              console.error('Error updating load status:', statusUpdateError);
            }
          }

          // Update local state
          setLoadingProgress(updatedTasks);
        }
      } else if (activeTab === 'unloading') {
        updatedTasks = [...unloadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].status = 'pending';
          updatedTasks[taskIndex].completed_at = null;
          updatedTasks[taskIndex].completed_by = null;
          if(item.requires_document) {
            if(item.task_key === 'deliveryDocumentsUpload') {
              updatedTasks[taskIndex].uploaded_documents = {};
            }
            else if(item.task_key === 't1DocumentUpload') {
              updatedTasks[taskIndex].uploaded_documents = { t1Document: null };
            }
          }

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              unloading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating task status:', updateError);
            alert('Görev durumu güncellenirken bir hata oluştu.');
            setIsUndoingTask(false);
            return;
          }

          // Check if load status should be changed from "completed" to "assigned"
          const hasAnyPendingTask = 
            loadingProgress.some(task => task.status === 'pending') ||
            updatedTasks.some(task => task.status === 'pending');

          if (load.status === 'completed' && hasAnyPendingTask) {
            const { error: statusUpdateError } = await supabase
              .from('loads')
              .update({ status: 'assigned' })
              .eq('id', load.id);

            if (statusUpdateError) {
              console.error('Error updating load status:', statusUpdateError);
            }
          }

          // Update local state
          setUnloadingProgress(updatedTasks);
        }
      }

      setIsUndoingTask(false);
    } catch (error) {
      console.error('Error undoing task:', error);
      alert('Görev geri alımı sırasında bir hata oluştu.');
      setIsUndoingTask(false);
    }
  };

  // Function to handle marking a task as complete
  const handleMarkComplete = async (item) => {
    try {
      // Set completing task state to true to show loading indicator
      setIsCompletingTask(true);

      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) throw userError;

/* 
      // Check if task requires documents
      if (item.task_key === 'documentsUpload' || item.task_key === 'deliveryDocumentsUpload') {
        const missingDocuments = item.document_types.filter(
          (docType) => !item.uploaded_documents?.[docType]
        );

        if (missingDocuments.length > 0) {
          alert(
            `Lütfen aşağıdaki belgeleri yükleyiniz:\n${missingDocuments.join('\n')}`
          );
          setIsCompletingTask(false);
          return;
        }
      }
 */

      // Special handling for loadingCompleted task
      if (item.task_key === 'loadingCompleted') {
        // Get package count from the load data
        const packageCount = load.load_details?.quantity;

        if (packageCount) {
          // Show confirmation dialog
          if (
            !window.confirm(
              `Yükleme tamamlandı mı? ${packageCount} paket yüklendi mi?`
            )
          ) {
            setIsCompletingTask(false);
            return;
          }
        }
      }

      // Update the task status
      let updatedTasks;
      if (activeTab === 'loading') {
        updatedTasks = [...loadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].status = 'completed';
          updatedTasks[taskIndex].completed_at = new Date().toISOString();
          updatedTasks[taskIndex].completed_by = user.id;

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              loading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating task status:', updateError);
            alert('Görev durumu güncellenirken bir hata oluştu.');
            setIsCompletingTask(false);
            return;
          }

          // Update local state
          setLoadingProgress(updatedTasks);

          // Check if all loading tasks are completed
          const allLoadingTasksCompleted = updatedTasks.every(
            (task) => task.status === 'completed'
          );

          // Fetch the current load to check unloading tasks
          const { data: loadData, error: fetchError } = await supabase
            .from('loads')
            .select('unloading_tasks')
            .eq('id', load.id)
            .single();

          if (fetchError) {
            console.error('Error fetching load:', fetchError);
            setIsCompletingTask(false);
            return;
          }

          // Check if all unloading tasks are completed
          const unloadingTasks = loadData?.unloading_tasks || [];
          const allUnloadingTasksCompleted = unloadingTasks.length > 0
            ? unloadingTasks.every((task) => task.status === 'completed')
            : false;

          // Update load status to completed only if both loading and unloading tasks are completed
          if (allLoadingTasksCompleted && allUnloadingTasksCompleted) {
            const { error: statusError } = await supabase
              .from('loads')
              .update({
                status: 'completed',
              })
              .eq('id', load.id);

            if (statusError) {
              console.error('Error updating load status:', statusError);
            } else {
              console.log('Load status updated to completed');
            }
          }
        }
      } else if (activeTab === 'unloading') {
        updatedTasks = [...unloadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].status = 'completed';
          updatedTasks[taskIndex].completed_at = new Date().toISOString();
          updatedTasks[taskIndex].completed_by = user.id;

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              unloading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating task status:', updateError);
            alert('Görev durumu güncellenirken bir hata oluştu.');
            setIsCompletingTask(false);
            return;
          }

          // Update local state
          setUnloadingProgress(updatedTasks);

          // Check if all unloading tasks are completed
          const allUnloadingTasksCompleted = updatedTasks.every(
            (task) => task.status === 'completed'
          );

          // Fetch the current load to check loading tasks
          const { data: loadData, error: fetchError } = await supabase
            .from('loads')
            .select('loading_tasks')
            .eq('id', load.id)
            .single();

          if (fetchError) {
            console.error('Error fetching load:', fetchError);
            setIsCompletingTask(false);
            return;
          }

          // Check if all loading tasks are completed
          const loadingTasks = loadData?.loading_tasks || [];
          const allLoadingTasksCompleted = loadingTasks.length > 0
            ? loadingTasks.every((task) => task.status === 'completed')
            : false;

          // Update load status to completed only if both loading and unloading tasks are completed
          if (allLoadingTasksCompleted && allUnloadingTasksCompleted) {
            const { error: statusError } = await supabase
              .from('loads')
              .update({
                status: 'completed',
              })
              .eq('id', load.id);

            if (statusError) {
              console.error('Error updating load status:', statusError);
            } else {
              console.log('Load status updated to completed');
            }
          }
        }
      }

      // Set completing task state back to false
      setIsCompletingTask(false);
    } catch (error) {
      console.error('Error marking task as complete:', error);
      alert('Görev tamamlanırken bir hata oluştu. Lütfen tekrar deneyin.');
      setIsCompletingTask(false);
    }
  };

  const [editingTaskKey, setEditingTaskKey] = useState(null);
  const [editedCompletionDate, setEditedCompletionDate] = useState('');
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);

  // Tarih güncelleme fonksiyonu
  const handleUpdateCompletionDate = async (item) => {
    try {
      setIsUpdatingDate(true);

      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Validate date
      if (!editedCompletionDate) {
        alert('Lütfen bir tarih seçiniz.');
        setIsUpdatingDate(false);
        return;
      }

      // Update the task completion date
      let updatedTasks;
      if (activeTab === 'loading') {
        updatedTasks = [...loadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].completed_at = new Date(editedCompletionDate).toISOString();

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              loading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating completion date:', updateError);
            alert('Tamamlanma tarihi güncellenirken bir hata oluştu.');
            setIsUpdatingDate(false);
            return;
          }

          // Update local state
          setLoadingProgress(updatedTasks);
        }
      } else if (activeTab === 'unloading') {
        updatedTasks = [...unloadingProgress];
        const taskIndex = updatedTasks.findIndex(
          (task) => task.task_key === item.task_key
        );

        if (taskIndex !== -1) {
          updatedTasks[taskIndex].completed_at = new Date(editedCompletionDate).toISOString();

          // Update the database
          const { error: updateError } = await supabase
            .from('loads')
            .update({
              unloading_tasks: updatedTasks,
            })
            .eq('id', load.id);

          if (updateError) {
            console.error('Error updating completion date:', updateError);
            alert('Tamamlanma tarihi güncellenirken bir hata oluştu.');
            setIsUpdatingDate(false);
            return;
          }

          // Update local state
          setUnloadingProgress(updatedTasks);
        }
      }

      // Reset editing state
      setEditingTaskKey(null);
      setEditedCompletionDate('');
      setIsUpdatingDate(false);
    } catch (error) {
      console.error('Error updating completion date:', error);
      alert('Tamamlanma tarihi güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
      setIsUpdatingDate(false);
    }
  };

  // Düzenleme modunu başlat
  const startEditingDate = (item) => {
    setEditingTaskKey(item.task_key);
    // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
    const date = new Date(item.completed_at);
    const localDateTime = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
      .toISOString()
      .slice(0, 16);
    setEditedCompletionDate(localDateTime);
  };

  // Düzenleme modunu iptal et
  const cancelEditingDate = () => {
    setEditingTaskKey(null);
    setEditedCompletionDate('');
  };


  // Function to get the first pending task index
  const getFirstPendingTaskIndex = (tasks) => {
    return tasks.findIndex((task) => task.status === 'pending');
  };

  // Render progress items with interactive elements
  const renderProgressItems = (items) => {
    const firstPendingTaskIndex = getFirstPendingTaskIndex(items);

    return (
      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${
              item.status === 'completed'
                ? 'bg-green-50 border-green-200'
                : index === firstPendingTaskIndex
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-medium">
                  {item.task_key === 'arrivedAtLoadingPoint'
                    ? 'Yükleme noktasına ulaşıldı'
                    : item.task_key === 'loadingStarted'
                      ? 'Yükleme başlatıldı'
                      : item.task_key === 'loadingCompleted'
                        ? 'Yükleme tamamlandı'
                        : item.task_key === 'arrivedAtExitCustoms'
                          ? 'Çıkış noktasına ulaşıldı'
                          : item.task_key === 'documentsUpload'
                            ? 'CMR Belgesi'
                            : item.task_key === 'arrivedAtArrivalCustoms'
                              ? 'Gümrük noktasına ulaşıldı'
                              : item.task_key === 'customsProceduresCompleted'
                                ? 'Gümrük işlemleri tamamlandı'
                                : item.task_key === 'arrivedAtUnloadingPoint'
                                  ? 'Boşaltma noktasına ulaşıldı'
                                  : item.task_key === 'deliveryDocumentsUpload'
                                    ? 'CMR Belgesi'
                                    : item.task_key === 'unloadingComplete'
                                      ? 'Boşaltma tamamlandı'
                                      : 'Diğer Belgeler'}
                </h4>
                {item.completed_at && (
                  <div className="mt-2">
                    {editingTaskKey === item.task_key ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="datetime-local"
                          value={editedCompletionDate}
                          onChange={(e) => setEditedCompletionDate(e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        />
                        <button
                          onClick={() => handleUpdateCompletionDate(item)}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center"
                          disabled={isUpdatingDate}
                        >
                          {isUpdatingDate ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                              Güncelleniyor...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCheck} className="mr-1" />
                              Onayla
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelEditingDate}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm flex items-center"
                          disabled={isUpdatingDate}
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-1" />
                          İptal
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <p className="text-sm text-gray-600">
                          Tamamlandı: {formatDate(item.completed_at)}
                        </p>
                        <button
                          onClick={() => startEditingDate(item)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title="Tarihi Düzenle"
                        >
                          <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {item.requires_document && (
                  <div className="mt-2">
                    <p className="text-sm text-blue-600 mb-2">
                      Gerekli Belgeler:
                    </p>
                    <div className="space-y-2">
                      {item.document_types?.map((docType) => (
                        <div
                          key={docType}
                          className="flex items-center justify-between bg-white p-2 rounded border border-gray-200"
                        >
                          <span className="text-sm flex items-center">
                            {docType}
                            {item.uploaded_documents?.[docType] && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 ml-2 text-green-500"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </span>
                          {item.uploaded_documents?.[docType] ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setPdfUrl(item.uploaded_documents[docType]);
                                  setIsPDFOpen(true);
                                }} 
                                className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 rounded bg-blue-50 flex items-center"
                                disabled={isUploading}
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="mr-1"
                                />
                                Görüntüle
                              </button>
                              <button
                                onClick={() =>
                                  handleDocumentUpload(item, docType)
                                }
                                className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 rounded bg-gray-50 flex items-center"
                                disabled={isUploading}
                              >
                                {isUploading ? (
                                  <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className="mr-1"
                                  />
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faExchangeAlt}
                                    className="mr-1"
                                  />
                                )}
                                Değiştir
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() =>
                                handleDocumentUpload(item, docType)
                              }
                              className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 rounded bg-blue-50 flex items-center"
                              disabled={isUploading}
                            >
                              {isUploading ? (
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  spin
                                  className="mr-1"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faUpload}
                                  className="mr-1"
                                />
                              )}
                              Yükle
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center ml-4">
                {item.status === 'completed' ? (
                  <>
                    <button
                      onClick={() => handleUndoTask(item)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                      disabled={isUndoingTask}
                    >Geri Al</button>
                    <span className="text-green-600 bg-green-50 p-2 rounded-full">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                  </>
                ) : index === firstPendingTaskIndex ? (
                  <button
                    onClick={() => handleMarkComplete(item)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                    disabled={isCompletingTask}
                  >
                    {isCompletingTask ? (
                      <>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        İşleniyor...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        Tamamla
                      </>
                    )}
                  </button>
                ) : (
                  <span className="text-gray-400 bg-gray-100 p-2 rounded-full">
                    <FontAwesomeIcon icon={faClock} className="h-6 w-6" />
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        <PDFViewer
          isOpen={isPDFOpen}
          onClose={() => setIsPDFOpen(false)}
          documentUrl={pdfUrl}
        />
      </div>
    );
  };

  const [expenses, setExpenses] = useState([]);
  const [expensesTypes, setExpensesTypes] = useState(true);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [countries, setCountries] = useState([]);

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setIsExpenseModalOpen(true);
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Bu masraf kaydını silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('load_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchExpenses();
    } catch (err) {
      console.error(err);
      alert('Silme işlemi başarısız.');
    }
  };

  const handleAddNewClick = () => {
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
  };

  const fetchExpenses = async () => {
    if (!load) return;
    const { data, error } = await supabase
      .from('load_expenses')
      .select('*')
      .eq('load_id', load.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching expenses:', error);
      return [];
    }
    setExpenses(data || []);
  };

  const fetchExpenseTypes = async () => {
    const { data, error } = await supabase
      .from('load_expenses_types')
      .select('*');

    if (error) {
      console.error('Error fetching expense types:', error);
      return [];
    }
    setExpensesTypes(data || []);
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase.from('countries').select('*');
      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };


  useEffect(() => {
    fetchExpenseTypes();
    fetchExpenses();
    fetchCountries();
  }, [load]);

  // Add new section to render load information
  const renderLoadInformation = () => {
    if (!load) return null;

    return (
      <div className="space-y-6">
        {/* Reference Information */}
        <div className='pb-3'>
          <h3 className="text-lg font-medium mb-3">Referans Bilgileri</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Referans Kodu</p>
              <p className="font-medium">{load.formatted_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Müşteri</p>
              <p className="font-medium">{load.customer_name}</p>
            </div>
          </div>
        </div>

        {/* Operations Officer Information - NEW SECTION */}
        {load?.operation_auth_id && (
          <div className='py-3'>
            <h3 className="text-lg font-medium mb-3">Operasyon Yetkilisi</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">İsim Soyisim</p>
                <p className="font-medium">
                  {operationOfficer?.name + ' ' + operationOfficer?.surname ||
                    'Atanmamış'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Telefon</p>
                <p className="font-medium">{operationOfficer?.phone || '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product Details */}
        <div className='py-3'>
          <h3 className="text-lg font-medium mb-3">Ürün Bilgileri</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Ürün</p>
              <p className="font-medium">{load.load_details?.product}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Miktar</p>
              <p className="font-medium">{load.load_details?.quantity}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Paket Tipi</p>
              <p className="font-medium">{load.load_details?.package_type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Net Ağırlık</p>
              <p className="font-medium">{load.load_details?.net_weight} kg</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Brüt Ağırlık</p>
              <p className="font-medium">
                {load.load_details?.gross_weight} kg
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Ürün Değeri</p>
              <p className="font-medium">
                {load.load_details?.goods_value} {load.goods_currency}
              </p>
            </div>
          </div>
        </div>

        {/* Loading Information */}
        <div className='py-3'>
          <h3 className="text-lg font-medium mb-3">Yükleme Bilgileri</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Yükleme Konumu</p>
              <p className="font-medium">{load.sender_company}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Yükleme Adresi</p>
              <p className="font-medium">{load.pickup_location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Yükleme Şehri</p>
              <p className="font-medium">{load.loading_city}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Yükleme Ülkesi</p>
              <p className="font-medium">{load.loading_country}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Yükleme Talimatları</p>
              <p className="font-medium">{load.loading_instructions}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Yükleme Gümrük</p>
              <p className="font-medium">
                {loadingCustoms ? (
                  <>
                    {loadingCustoms.customs_name} - {loadingCustoms.city},{' '}
                    {loadingCustoms.country}
                    <br />
                    <span className="text-xs text-gray-500">
                      Kod: {loadingCustoms.customs_code}
                    </span>
                  </>
                ) : (
                  'Gümrük bilgisi yok'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Discharge Information */}
        <div className='py-3'>
          <h3 className="text-lg font-medium mb-3">Boşaltma Bilgileri</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Boşaltma Konumu</p>
              <p className="font-medium">{load.receiver_company}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Boşaltma Adresi</p>
              <p className="font-medium">{load.delivery_location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Boşaltma Şehri</p>
              <p className="font-medium">{load.unloading_city}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Boşaltma Ülkesi</p>
              <p className="font-medium">{load.discharge_country}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Boşaltma Talimatları</p>
              <p className="font-medium">{load.discharge_instructions}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Boşaltma Gümrük</p>
              <p className="font-medium">
                {dischargeCustoms ? (
                  <>
                    {dischargeCustoms.customs_name} - {dischargeCustoms.city},{' '}
                    {dischargeCustoms.country}
                    <br />
                    <span className="text-xs text-gray-500">
                      Kod: {dischargeCustoms.customs_code}
                    </span>
                  </>
                ) : (
                  'Gümrük bilgisi yok'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Driver and Vehicle Information */}
        {load.status === 'assigned' && (
          <div className='py-3'>
            <h3 className="text-lg font-medium mb-3">
              Sürücü & Araç Bilgileri
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Sürücü Adı</p>
                <p className="font-medium">
                  {load.accepted_driver?.first_name +
                    ' ' +
                    load.accepted_driver?.last_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Sürücü Telefonu</p>
                <p className="font-medium">
                  {load.accepted_driver?.phone_number}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600">Çekici Plakası</p>
                  {!isEditingPlates && (
                    <button
                      onClick={() => {
                        setEditTowPlate(load.accepted_driver?.tow_plate || '');
                        setEditTrailerPlate(load.accepted_driver?.trailer_plate || '');
                        setIsEditingPlates(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                      title="Plakaları Düzenle"
                    >
                      <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {isEditingPlates ? (
                  <input
                    type="text"
                    value={editTowPlate}
                    onChange={(e) => setEditTowPlate(e.target.value)}
                    className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm w-full"
                    placeholder="Çekici plakası"
                  />
                ) : (
                  <p className="font-medium">{load.accepted_driver?.tow_plate}</p>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600">Dorse Plakası</p>
                  {!isEditingPlates && (
                    <button
                      onClick={() => {
                        setEditTowPlate(load.accepted_driver?.tow_plate || '');
                        setEditTrailerPlate(load.accepted_driver?.trailer_plate || '');
                        setIsEditingPlates(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                      title="Plakaları Düzenle"
                    >
                      <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {isEditingPlates ? (
                  <div>
                    <input
                      type="text"
                      value={editTrailerPlate}
                      onChange={(e) => setEditTrailerPlate(e.target.value)}
                      className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      placeholder="Dorse plakası"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleSavePlates}
                        disabled={isSavingPlates}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center"
                      >
                        {isSavingPlates ? (
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                        ) : (
                          <FontAwesomeIcon icon={faCheck} className="mr-1" />
                        )}
                        {isSavingPlates ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                      <button
                        onClick={() => setIsEditingPlates(false)}
                        disabled={isSavingPlates}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm flex items-center"
                      >
                        <FontAwesomeIcon icon={faTimes} className="mr-1" />
                        İptal
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="font-medium">{load.accepted_driver?.trailer_plate}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Additional Transport Details */}
        <div className='py-3'>
          <h3 className="text-lg font-medium mb-3">Taşıma Detayları</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Kargo Tipi</p>
              <p className="font-medium">{load.cargo_type}</p>
            </div>
          </div>
        </div>

        {/* Kilometer and Fuel Information */}
        {(load.starting_km || load.final_km || load.starting_fuel || load.final_fuel) && (
          <div className='py-3'>
            <h3 className="text-lg font-medium mb-3">Kilometre ve Yakıt Bilgileri</h3>
            <div className="grid grid-cols-3 gap-4">
              {/* Kilometer Information */}
              <div>
                <p className="text-sm text-gray-600">Başlangıç KM</p>
                <p className="font-medium">{load.starting_km ? load.starting_km.toLocaleString('tr-TR') : '-'} km</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bitiş KM</p>
                <p className="font-medium">{load.final_km ? load.final_km.toLocaleString('tr-TR') : '-'} km</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">KM Farkı</p>
                <p className={`font-medium ${(load.final_km - load.starting_km)}`}>
                  {load.starting_km && load.final_km 
                    ? (load.final_km - load.starting_km).toLocaleString('tr-TR') 
                    : '-'} km
                </p>
              </div>
              
              {/* Fuel Information */}
              <div>
                <p className="text-sm text-gray-600">Başlangıç Yakıt</p>
                <p className="font-medium">{load.starting_fuel ? load.starting_fuel.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'} L</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bitiş Yakıt</p>
                <p className="font-medium">{load.final_fuel ? load.final_fuel.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'} L</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Yakıt Farkı</p>
                <p className={`font-medium ${(load.final_fuel - load.starting_fuel)}`}>
                  {load.starting_fuel && load.final_fuel 
                    ? (load.final_fuel - load.starting_fuel).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) 
                    : '-'} L
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderExpenses = () => {
    if (expenses.length === 0) {
      return (
        <>
          <div className="space-y-4">
            <p className="text-gray-500 text-center py-4">Henüz masraf kaydı bulunmuyor.</p>
          </div>
          <div className="flex justify-center">
            <button 
              onClick={handleAddNewClick}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center shadow-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Masraf
            </button>
          </div>
          <ExpenseModal
            isOpen={isExpenseModalOpen}
            onClose={() => setIsExpenseModalOpen(false)}
            onSave={() => fetchExpenses()}
            expenseToEdit={selectedExpense}
            expenseTypes={expensesTypes}
            fromLoads={true}

            loadId={load.id}
            driverId={load.accepted_driver?.id}
          />
        </>
      );
    }

    const getExpenseTypeName = (expenseTypeId) => {
      const type = expensesTypes.find(t => t.id === expenseTypeId);
      return type ? type.name_tr : 'Bilinmeyen';
    };

    const getCountryName = (countryId) => {
      const country = countries.find(c => c.id === countryId);
      return country ? country.name_tr : 'Bilinmeyen';
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatAmount = (amount, currency) => {
      if (currency === 'Other' || !currency) {
        return new Intl.NumberFormat('tr-TR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
      }

      try {
        return new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: currency
        }).format(amount);
      } catch (e) {
        return new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' ' + currency;
      }
    };

    const calculateTotalAmount = (expense) => {
      if (expense.exchange_rate) {
        return expense.amount * expense.exchange_rate;
      }
      return null;
    };

    // Toplam masraf hesaplama
    const calculateTotalExpenses = () => {
      let totalTRY = 0;
      const withoutRate = {};

      expenses.forEach((expense) => {
        if (expense.currency_type === 'TRY') {
          totalTRY += expense.amount;
        } else if (expense.exchange_rate) {
          totalTRY += expense.amount * expense.exchange_rate;
        } else {
          // Kur yoksa ayrı tut
          const currency = expense.currency_type;
          if (!withoutRate[currency]) {
            withoutRate[currency] = 0;
          }
          withoutRate[currency] += expense.amount;
        }
      });

      return { totalTRY, withoutRate };
    };

    const { totalTRY, withoutRate } = calculateTotalExpenses();

    return (
      <>
        <div className="space-y-4">
          {/* Toplam Masraf Özeti */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Masraf Özeti</h3>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Toplam Masraf:</span>
                <span className="text-lg font-bold text-blue-700">
                  {new Intl.NumberFormat('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(totalTRY)} TRY
                </span>
              </div>
              
              {Object.keys(withoutRate).length > 0 && (
                <div className="pt-2 border-t border-blue-200">
                  <span className="text-xs font-medium text-amber-700">Kur Belirlenmemiş:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(withoutRate).map(([currency, amount]) => (
                      <span key={currency} className="inline-block px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-800 rounded">
                        {new Intl.NumberFormat('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(amount)} {currency}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {expenses.map((expense) => (
            <div 
              key={expense.id} 
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      {getExpenseTypeName(expense.expense_type)}
                    </span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatAmount(expense.amount, expense.currency_type)}
                    </span>
                  </div>
                  
                  {/* Ülke Bilgisi */}
                  {expense.country && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-gray-500">Ülke:</span>
                      <span className="text-xs font-medium text-gray-700">
                        {getCountryName(expense.country)}
                      </span>
                    </div>
                  )}

                  {/* Toplam Masraf (exchange_rate varsa) */}
                  {(expense.currency_type !== 'TRY') && calculateTotalAmount(expense) && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-gray-500">Toplam Masraf:</span>
                      <span className="text-xs font-semibold text-green-700">
                        {new Intl.NumberFormat('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(calculateTotalAmount(expense))} TRY
                      </span>
                      <span className="text-xs text-gray-400">
                        (Kur: {expense.exchange_rate})
                      </span>
                    </div>
                  )}

                  {expense.description && (
                    <p className="text-gray-600 text-sm mt-1">{expense.description}</p>
                  )}
                </div>
                <div className="flex items-center justify-end space-x-5">
                  <button 
                    onClick={() => handleEditClick(expense)}
                    className="text-indigo-600 hover:text-indigo-900"
                    title="Düzenle"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button 
                    onClick={() => handleDeleteExpense(expense.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Sil"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>
              
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  {formatDate(expense.expense_time)}
                </span>
                {expense.filepath && (
                  <button 
                    onClick={() => {
                      setPdfUrl(expense.filepath);
                      setIsPDFOpen(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Fişi Görüntüle
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="flex justify-center">
            <button 
              onClick={handleAddNewClick}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center shadow-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Masraf
            </button>
          </div>

        </div>
        <ExpenseModal
          isOpen={isExpenseModalOpen}
          onClose={() => setIsExpenseModalOpen(false)}
          onSave={() => fetchExpenses()}
          expenseToEdit={selectedExpense}
          expenseTypes={expensesTypes}
          fromLoads={true}

          loadId={load.id}
          driverId={load.accepted_driver?.id}
        />
        <PDFViewer
          isOpen={isPDFOpen}
          onClose={() => setIsPDFOpen(false)}
          documentUrl={pdfUrl}
        />
      </>
    );
  };

  const renderHistory = () => {
    const getTaskName = (task) => {
      const loadingTaskNames = {
        arrivedAtLoadingPoint: 'Yükleme noktasına ulaşıldı',
        loadingStarted: 'Yükleme başlatıldı',
        loadingCompleted: 'Yükleme tamamlandı',
        arrivedAtExitCustoms: 'Çıkış gümrüğüne ulaşıldı',
        documentsUpload: 'Belge yüklendi',
      };

      const unloadingTaskNames = {
        arrivedAtArrivalCustoms: 'Varış gümrüğüne ulaşıldı',
        customsProceduresCompleted: 'Gümrük işlemleri tamamlandı',
        arrivedAtUnloadingPoint: 'Boşaltma noktasına ulaşıldı',
        deliveryDocumentsUpload: 'Teslimat belgeleri yüklendi',
        unloadingComplete: 'Boşaltma tamamlandı',
      };

      return (
        loadingTaskNames[task.task_key] ||
        unloadingTaskNames[task.task_key] ||
        task.task_key
      );
    };

    return (
      <div className="space-y-4">
        {/* Loading Tasks */}
        {loadingProgress
          .filter((task) => task.status === 'completed')
          .map((task, index) => (
            <div
              key={`loading-${index}`}
              className="p-4 rounded-lg border bg-green-50 border-green-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{getTaskName(task)}</p>
                  <div className="flex items-center mt-1 text-sm text-gray-600">
                    {task.completed_at ? (
                      <>
                        <span className="mr-2">
                          {formatDate(task.completed_at)}
                        </span>
                        <span>•</span>
                        <span className="ml-2">
                          {historyUsers[task.completed_by] ||
                            'Sürücü tarafından tamamlandı'}
                        </span>
                      </>
                    ) : (
                      <span>Beklemede</span>
                    )}
                  </div>
                  {task.requires_document && task.uploaded_documents && (
                    <div className="mt-2 text-sm text-blue-600">
                      Yüklenen Belgeler:{' '}
                      {Object.keys(task.uploaded_documents).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

        {/* Unloading Tasks */}
        {unloadingProgress
          .filter((task) => task.status === 'completed')
          .map((task, index) => (
            <div
              key={`unloading-${index}`}
              className="p-4 rounded-lg border bg-green-50 border-green-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{getTaskName(task)}</p>
                  <div className="flex items-center mt-1 text-sm text-gray-600">
                    {task.completed_at ? (
                      <>
                        <span className="mr-2">
                          {formatDate(task.completed_at)}
                        </span>
                        <span>•</span>
                        <span className="ml-2">
                          {historyUsers[task.completed_by] ||
                            'Bilinmeyen Kullanıcı'}
                        </span>
                      </>
                    ) : (
                      <span>Beklemede</span>
                    )}
                  </div>
                  {task.requires_document && task.uploaded_documents && (
                    <div className="mt-2 text-sm text-blue-600">
                      Yüklenen Belgeler:{' '}
                      {Object.keys(task.uploaded_documents).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

        {/* Load Creation Event */}
        <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Yük oluşturuldu</p>
              <div className="flex items-center mt-1 text-sm text-gray-600">
                <span className="mr-2">{formatDate(load.created_at)}</span>
                <span>•</span>
                <span className="ml-2">
                  {historyUsers[load.created_by] || 'Bilinmeyen Kullanıcı'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {loadingProgress.length === 0 && unloadingProgress.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            Henüz görev kaydı bulunmamaktadır.
          </div>
        )}
      </div>
    );
  };

  // Document viewer modal content
  if (selectedDocument || selectedTaskDocument) {
    const document = selectedDocument || selectedTaskDocument;
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white p-6 rounded-lg max-w-4xl w-full h-[80vh] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">{document.name}</h2>
              <p className="text-sm text-gray-600">
                Kategori: {document.category}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedDocument(null);
                setSelectedTaskDocument(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Detaylara Dön
            </button>
          </div>

          <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden">
            <iframe
              src={document.url}
              title={document.name}
              className="w-full h-full border-0"
            />
          </div>
        </div>
      </Modal>
    );
  }

  // Original details modal content
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-6">Yük Detayları</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {['details', 'loading', 'unloading', 'expenses', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab === 'details'
                  ? 'Detaylar'
                  : tab === 'loading'
                    ? 'Yükleme'
                    : tab === 'unloading'
                      ? 'Boşaltma'
                      : tab === 'expenses'
                      ? 'Masraflar'
                      : 'İşlem Geçmişi'}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderLoadInformation()}
        {activeTab === 'loading' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Yükleme İlerlemesi</h3>
            {renderProgressItems(loadingProgress)}
          </div>
        )}
        {activeTab === 'unloading' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Boşaltma İlerlemesi</h3>
            {renderProgressItems(unloadingProgress)}
          </div>
        )}
        {activeTab === 'expenses' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Masraflar</h3>
            {renderExpenses()}
          </div>
        )}
        {activeTab === 'history' && (
          <div>
            <h3 className="text-lg font-medium mb-4">İşlem Geçmişi</h3>
            {renderHistory()}
          </div>
        )}

        {renderMap()}

        <div className="flex justify-between mt-6">
          {load?.accepted_driver?.id && (
            <button
              onClick={handleSendNotification}
              disabled={isSendingNotification}
              className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${
                isSendingNotification ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSendingNotification
                ? 'Gönderiliyor...'
                : 'Konum Bildirimi Gönder'}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Kapat
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LoadDetailsModal;